<template>
  <div class="joszaki-data-table flex flex-col gap-2 md:text-xs">
    <JoszakiDataTableFilterEditorModal
      ref="filterEditorModal"
      :filter-options="filterOptions"
      @save="addFilter"
    />
    <JoszakiExportModal
      :open="exportModalOpen"
      :total-items="pagination?.total || 0"
      :current-page-items="items"
      :columns="columns"
      :displayed-columns="displayedColumns"
      :load-function="load"
      :fields="requestedFields"
      :current-filters="{
        search: searchString,
        orderBy: orderBy,
        order: order,
        filters: getFilterSubmitData(),
      }"
      @close="closeExportModal"
    />
    <div v-if="!!search || filterOptions.length" class="flex flex-row gap-2">
      <JoszakiInput
        v-model="searchString"
        icon-left="search"
        class="flex-1"
        :show-error-msg="false"
        :placeholder="search?.placeholder"
        :disabled="!search"
        @input="onSearchInput"
        @keyup.enter="fetch"
      />
      <JoszakiButton icon-left="search" type="info" @click="fetch" />
      <JoszakiButton
        icon-left="cloud-download-alt"
        type="info"
        :title="$t('conductor.dataTable.saveAsCsv')"
        @click="openExportModal"
      />
      <JoszakiDropdownButton
        type="info"
        icon-left="columns"
        icon-right="caret-down"
        checkboxes
        :close-on-select="false"
        :title="$t('conductor.dataTable.columns')"
        :items="columnsForDropdown"
        @itemSelected="changeColumnVisibility"
      />
      <JoszakiDropdownButton
        v-if="filterOptions?.length"
        type="info"
        icon-left="filter"
        icon-right="caret-down"
        value-key="id"
        :items="filterOptions"
        :title="$t('conductor.dataTable.addFilter')"
        @itemSelected="openAddFilter"
      />
    </div>
    <div v-if="filterValues?.length">
      <div class="font-bold">{{ $t("conductor.dataTable.filters") }}</div>
      <div class="flex flex-row flex-wrap gap-2">
        <JoszakiDataTableFilterPill
          v-for="(fv, index) in filterValues"
          :key="index"
          :filter-value="fv"
          :filter-options="filterOptions"
          @edit="openEditFilter(fv, index)"
          @remove="removeFilter(index)"
        />
        <JoszakiButton
          size="sm"
          icon-left="trash"
          type="error"
          @click="removeFilters"
        >
          {{ $t("conductor.dataTable.removeFilters") }}
        </JoszakiButton>
      </div>
    </div>
    <div class="flex-1 overflow-scroll max-h-[77vh] relative">
      <table
        class="max-h-full p-4 border-spacing-2 w-full relative border border-gray-300 dark:border-gray-600"
      >
        <thead class="bg-primary sticky top-0 z-[5]">
          <tr>
            <th
              v-for="(column, index) in visibleColumns"
              :key="index"
              class="p-2 text-white group text-left"
              :class="{
                'cursor-pointer': column.orderable,
                'sticky w-[100px] bg-primary': column.sticky,
              }"
              :style="{
                right: `${(visibleColumns.length - index - 1) * 100}px`,
              }"
              @click="onColumnHeaderClick(column)"
            >
              <JoszakiTooltip
                v-if="column.labelShort"
                :text="column.label"
                placement="bottom-start"
              >
                {{ column.labelShort }}
              </JoszakiTooltip>
              <template v-else>
                {{ column.label }}
              </template>
              <IconComponent
                v-if="column.orderable"
                :class="{ 'opacity-25': orderBy !== column.name }"
                :icon="order === 'asc' ? 'angle-down' : 'angle-up'"
                class="inline w-5"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="fetchState.pending">
            <tr
              v-for="i in 15"
              :key="i"
              class="[&:nth-child(even)]:bg-primary-lightest/30 hover:!bg-primary-lightest dark:[&:nth-child(even)]:bg-gray-700/30 dark:hover:!bg-gray-700"
            >
              <td
                v-for="(column, index) in visibleColumns"
                :key="index"
                class="p-4"
              >
                <content-placeholders>
                  <content-placeholders-text :lines="1" class="[&>*]:!mb-0" />
                </content-placeholders>
              </td>
            </tr>
          </template>
          <tr v-else-if="fetchState.error">
            <td :colspan="visibleColumns.length" class="p-2 text-error">
              {{ fetchState.error?.message }}
            </td>
          </tr>
          <tr v-else-if="items.length === 0">
            <td :colspan="visibleColumns.length" class="p-2">
              {{ $t("conductor.dataTable.noData") }}
            </td>
          </tr>
          <tr
            v-for="row in items"
            :key="`row-${row.id}`"
            class="[&:nth-child(even)]:bg-primary-lightest/30 hover:!bg-primary-lightest dark:[&:nth-child(even)]:bg-gray-700/30 dark:hover:!bg-gray-700"
          >
            <td
              v-for="(column, colIndex) in visibleColumns"
              :key="`column-${colIndex}`"
              class="p-2 align-middle truncate"
              :class="{
                'sticky !w-[100px] !bg-primary-lightest dark:!bg-gray-700':
                  column.sticky,
                'max-w-[200px] min-w-[70px]': !column.sticky,
              }"
              :style="{
                right: `${(visibleColumns.length - colIndex - 1) * 100}px`,
              }"
            >
              <span
                v-if="!$scopedSlots[`column-${column.name}`]"
                :title="
                  column.formatter
                    ? column.formatter(row)
                    : get(row, column.name, '-')
                "
              >
                <template v-if="column.formatter">
                  {{ column.formatter(row) }}
                </template>
                <template v-else-if="typeof get(row, column.name) === 'number'">
                  {{ $n(get(row, column.name)) }}
                </template>
                <template
                  v-else-if="typeof get(row, column.name) === 'boolean'"
                >
                  {{ get(row, column.name) ? "✓" : "✗" }}
                </template>
                <template v-else>
                  {{ get(row, column.name, "-") }}
                </template>
              </span>
              <slot :name="`column-${column.name}`" :row="row" />
            </td>
          </tr>
        </tbody>
        <tfoot class="sticky bottom-0 z-[5]">
          <tr v-if="summary && summaryValues" class="bg-primary-light">
            <td
              v-for="(column, index) in visibleColumns"
              :key="`summary-${index}`"
              class="p-2 text-white font-bold"
              :class="{
                'sticky !bg-primary-light': column.sticky,
              }"
              :style="{
                right: `${(visibleColumns.length - index - 1) * 100}px`,
              }"
            >
              {{ summaryValues[column.name] || "" }}
            </td>
          </tr>
          <tr v-if="pagination" class="bg-primary">
            <td :colspan="visibleColumns.length" class="p-2 text-white">
              {{
                $t("conductor.dataTable.total", {
                  total: pagination?.total,
                  pages: pagination?.last,
                })
              }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div class="pt-2 flex flex-col md:flex-row justify-between">
      <JoszakiDropdown
        v-model="pageSize"
        :items="pageSizeOptions"
        @select="onPageSizeChange"
      />
      <PaginationComponent
        v-if="pagination"
        show-first-last
        :pagination-info="pagination"
        :increment="4"
        :use-hash-params="false"
      />
    </div>
  </div>
</template>

<script>
import {
  ref,
  useFetch,
  useRoute,
  useRouter,
  useContext,
  computed,
} from "@nuxtjs/composition-api";
import { watch } from "vue";
import get from "lodash/get";
import debounce from "lodash/debounce";
import difference from "lodash/difference";
import isEqual from "lodash/isEqual";
import uniq from "lodash/uniq";
import PaginationComponent from "~/components/_refactored/common/PaginationComponent.vue";
import JoszakiTooltip from "~/components/_refactored/common/JoszakiTooltip.vue";
import JoszakiDataTableFilterPill from "~/components/_refactored/conductor/data-table/JoszakiDataTableFilterPill.vue";
import JoszakiDataTableFilterEditorModal from "~/components/_refactored/conductor/data-table/JoszakiDataTableFilterEditorModal.vue";
import JoszakiExportModal from "~/components/_refactored/conductor/data-table/JoszakiExportModal.vue";
import { useDataTableFilter } from "~/components/_refactored/conductor/data-table/useDataTableFilter";

export default {
  components: {
    PaginationComponent,
    JoszakiDataTableFilterPill,
    JoszakiDataTableFilterEditorModal,
    JoszakiExportModal,
    JoszakiTooltip,
  },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    search: {
      type: Object,
      default: () => null,
    },
    filterOptions: {
      type: Array,
      default: () => [],
    },
    defaultFilters: {
      type: Array,
      default: () => [],
    },
    orderOptions: {
      type: Object,
      default: () => ({
        orderBy: "id",
        order: "asc",
      }),
    },
    load: {
      type: Function,
      required: true,
    },
    summary: {
      type: Function,
      default: null,
    },
    requiredFields: {
      type: Array,
      default: () => ["id"],
    },
  },
  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const { i18n } = useContext();

    const pageSizeOptions = [15, 30, 50, 100, 250].map((pageSize) => ({
      value: pageSize,
      label: i18n.t("conductor.dataTable.perPage", { pageSize }),
    }));

    const query = route.value.query;
    const orderBy = ref(query?.orderBy ?? props.orderOptions.orderBy);
    const order = ref(query?.order ?? props.orderOptions.order);
    const searchString = ref(query?.search ?? "");
    const page = ref(Number(query?.page ?? 1));
    const pageSize = ref(Number(query?.pageSize ?? 15));
    const items = ref([]);
    const pagination = ref(null);
    const exportModalOpen = ref(false);
    const {
      filterValues,
      addFilter,
      filterEditorModal,
      getFilterSubmitData,
      openAddFilter,
      openEditFilter,
      removeFilter,
      removeFilters,
    } = useDataTableFilter(props.defaultFilters);

    const summaryValues = computed(() => {
      if (!props.summary || !items.value.length) {
        return null;
      }
      return props.summary(items.value);
    });

    const displayedColumns = ref(
      query?.columns
        ? query.columns.split(",")
        : props.columns
            .filter((column) => !column.hidden)
            .map((column) => column.name)
    );
    const visibleColumns = computed(() =>
      props.columns.filter((column) =>
        displayedColumns.value.includes(column.name)
      )
    );

    const requestedFields = computed(() =>
      uniq([
        ...props.requiredFields,
        ...visibleColumns.value.flatMap(
          (column) => column.fields ?? [column.name]
        ),
      ])
    );
    let lastFetchedFields = [];

    const columnsForDropdown = computed(() =>
      props.columns.map((column) => {
        const isDisplayed = displayedColumns.value.includes(column.name);
        return {
          value: column.name,
          label: column.label,
          isSelected: isDisplayed,
        };
      })
    );

    const changeColumnVisibility = (column) => {
      const index = displayedColumns.value.indexOf(column.value);
      if (index !== -1) {
        displayedColumns.value.splice(index, 1);
      } else {
        displayedColumns.value.push(column.value);
      }

      router.push({
        query: {
          ...route.value.query,
          columns: displayedColumns.value.join(","),
        },
      });
    };

    const { fetch, fetchState } = useFetch(async () => {
      items.value = [];
      const fields = requestedFields.value;
      const response = await props.load(
        {
          page: page.value,
          pageSize: pageSize.value,
          search: searchString.value,
          orderBy: orderBy.value,
          order: order.value,
          filters: getFilterSubmitData(),
        },
        { fields }
      );

      lastFetchedFields = fields;
      pagination.value = response.pagination;
      items.value = response.items;
    });

    const onlyColumnsChanged = (query, oldQuery) => {
      const changedKeys = Object.keys({ ...query, ...oldQuery }).filter(
        (key) => !isEqual(query?.[key], oldQuery?.[key])
      );
      return changedKeys.length === 1 && changedKeys[0] === "columns";
    };

    watch(
      () => route.value.query,

      (query, oldQuery) => {
        page.value = Number(query?.page ?? 1);
        if (
          onlyColumnsChanged(query, oldQuery) &&
          difference(requestedFields.value, lastFetchedFields).length === 0
        ) {
          return;
        }
        fetch();
      }
    );

    const onColumnHeaderClick = (column) => {
      if (!column.orderable) {
        return;
      }

      if (orderBy.value === column.name) {
        order.value = order.value === "asc" ? "desc" : "asc";
      } else {
        orderBy.value = column.name;
        order.value = "asc";
      }

      router.push({
        query: {
          ...route.value.query,
          orderBy: orderBy.value,
          order: order.value,
          page: 1,
        },
      });
    };

    const onSearchInput = debounce(async function (search) {
      if (search.length > 0 && search.length < 3) {
        return;
      }

      await router.push({
        query: {
          ...route.value.query,
          search,
          page: 1,
        },
      });
    }, 500);

    const onPageSizeChange = (pageSize) => {
      router.push({
        query: {
          ...route.value.query,
          pageSize: pageSize.value,
          page: 1,
        },
      });
    };

    const openExportModal = () => {
      exportModalOpen.value = true;
    };

    const closeExportModal = () => {
      exportModalOpen.value = false;
    };

    return {
      pageSizeOptions,
      orderBy,
      order,
      searchString,
      page,
      pageSize,
      items,
      pagination,
      fetch,
      fetchState,
      onColumnHeaderClick,
      onSearchInput,
      onPageSizeChange,
      get,
      filterValues,
      columnsForDropdown,
      displayedColumns,
      visibleColumns,
      requestedFields,
      changeColumnVisibility,
      openAddFilter,
      openEditFilter,
      addFilter,
      removeFilter,
      removeFilters,
      filterEditorModal,
      getFilterSubmitData,
      exportModalOpen,
      openExportModal,
      closeExportModal,
      summaryValues,
    };
  },
  fetchOnServer: false,
};
</script>
